import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Backdrop,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Schedule as ScheduleIcon,
  Preview as PreviewIcon,
  AccountCircle as AccountIcon,
  Add as AddIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { loadSocialMediaAccounts, fetchMonthlyPostCount } from "../../Action/api";
import { getAuthTokenPayload } from "../../Utils/index";
import {
  previewRunLaterPost,
  submitForApprovalRunLaterPost,
} from "../../Action/runLaterApi";
import CalendarView from "../../Components/CalendarView";
import { postTemplates } from "../../MasterData/postTemplate";
import PreviewPostDialog from "../../Components/PreviewPostDialog";
import PlatformsSelect from "./components/PlatformsSelect";
import AccountsSelect from "./components/AccountsSelect";
import MediaUploadSection from "./components/MediaUploadSection";
import SafePostDialogs from "./components/SafePostDialogs";
import { useMediaAndCropping } from "./hooks/useMediaAndCropping";

// Back up Code 17 Oct 2025 (Very Important for Phase2)

// const platformConfigurations = {
//   "Content Only": ["Facebook", "Twitter (X)", "LinkedIn"],
//   Image: ["Facebook", "Instagram", "Twitter (X)", "LinkedIn"],
//   Video: ["Facebook", "Instagram", "Twitter (X)", "YouTube", "LinkedIn"],
// };

// Added on 17th Oct 2025 to remove Twitter and YouTube
const platformConfigurations = {
  "Content Only": ["Facebook", "LinkedIn"],
  Image: ["Facebook", "Instagram", "LinkedIn"],
  Video: ["Facebook", "Instagram", "LinkedIn"],
};

const getAvailablePlatforms = (type) => platformConfigurations[type] || [];
const convertPlatformForAPI = (p) => (p === "Twitter (X)" ? "Twitter" : p);

export const SafePost = () => {
  // Derive monthly post limit from JWT menuList (MENU-002)
  const { allowPerMonthPost } = (() => {
    const menuList = getAuthTokenPayload()?.menuList ?? [];
    const runPostMenu = menuList.find((m) => m.menuId === "MENU-002");
    return { allowPerMonthPost: runPostMenu?.allowPerMonthPost ?? null };
  })();

  // States
  const [socialMediaAccounts, setSocialMediaAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountsError, setAccountsError] = useState("");
  const [contentType, setContentType] = useState("");
  const [platforms, setPlatforms] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [postContent, setPostContent] = useState("");
  const [scheduledDateTime, setScheduledDateTime] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPhase, setLoadingPhase] = useState("");
  const [loadingMessage, setLoadingMessage] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalData, setErrorModalData] = useState({ title: "", accounts: [], scheduleDate: "" });
  const calendarDialogContentRef = React.useRef(null);
  const drawerContainerEl = null; // If not used, you can remove this line

  const {
    mediaFile,
    setMediaFile,
    isUploading,
    handleMediaUpload,
    removeMediaFile,
    removeAllMediaFiles,
    instagramCropperOpen,
    currentCroppingImage,
    handleInstagramCropperClose,
    handleInstagramCropSave,
    resetCropping,
  } = useMediaAndCropping({
    contentType,
    platforms,
    setSnackbarMessage,
    setSnackbarSeverity,
    setOpenSnackbar,
  });

  useEffect(() => {
    fetchSocialMediaAccounts();
    
    // Check if there's a scheduled date/time from calendar
    const storedScheduledDateTime = localStorage.getItem('scheduledDateTime');
    if (storedScheduledDateTime) {
      try {
        const dateTime = dayjs(storedScheduledDateTime);
        if (dateTime.isValid()) {
          setScheduledDateTime(dateTime);
          // Clear the stored value after using it
          localStorage.removeItem('scheduledDateTime');
          localStorage.removeItem('scheduleDate');
          localStorage.removeItem('scheduleTime');
        }
      } catch (error) {
        console.error('Error parsing scheduled date/time:', error);
      }
    }
  }, []);
  useEffect(() => {
    setPlatforms([]);
    setSelectedAccounts([]);
  }, [contentType]);

  const fetchSocialMediaAccounts = async () => {
    setLoadingAccounts(true);
    setAccountsError("");
    const clientId = getAuthTokenPayload()?.clientId;

    try {
      const result = await loadSocialMediaAccounts(clientId);
      if (result.success) {
        setSocialMediaAccounts(result.data.accounts || []);
      } else {
        setAccountsError(result.message);
        setSocialMediaAccounts([]);
      }
    } catch (error) {
      setAccountsError(
        "Failed to load social media accounts. Please try again."
      );
      setSocialMediaAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  // Handlers
  const handleContentTypeChange = (newContentType) => {
    setContentType(newContentType);
    setPlatforms([]);
    setSelectedAccounts([]);
  };

  const handlePlatformChange = (event) => {
    const value = event.target.value;
    const newPlatforms = typeof value === "string" ? value.split(",") : value;
    setPlatforms(newPlatforms);
    setSelectedAccounts([]);
  };

  const handleAccountChange = (event) => {
    const value = event.target.value;
    const accounts = typeof value === "string" ? value.split(",") : value;
    setSelectedAccounts(accounts);
  };

  // Template functions
  const selectTemplate = (template) => {
    setPostContent(template.content);
    setTemplateModalOpen(false);
  };
  const filteredTemplates = postTemplates.filter(
    (t) =>
      t.name.toLowerCase().includes(templateSearchQuery.toLowerCase()) ||
      t.title.toLowerCase().includes(templateSearchQuery.toLowerCase())
  );

  // Submission
  const handleSubmitForApproval = async () => {
    if (
      !contentType ||
      !postContent.trim() ||
      !scheduledDateTime ||
      !platforms.length ||
      !selectedAccounts.length
    ) {
      setSnackbarMessage("Please fill in all required fields");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }
    if (
      contentType !== "Content Only" &&
      (!mediaFile || (Array.isArray(mediaFile) && !mediaFile.length))
    ) {
      setSnackbarMessage(
        `Please upload at least one ${contentType.toLowerCase()} file`
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    // ── Monthly post limit check ────────────────────────────────
    if (allowPerMonthPost !== null && allowPerMonthPost > 0) {
      const _months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const _now = new Date();
      const _monthYear = `${_months[_now.getMonth()]}-${_now.getFullYear()}`;
      const _clientId = getAuthTokenPayload()?.clientId;
      const _limitResult = await fetchMonthlyPostCount(_clientId, _monthYear);
      if (_limitResult.success && _limitResult.data) {
        const { postCount } = _limitResult.data;
        if (postCount >= allowPerMonthPost) {
          setSnackbarMessage(
            `Monthly post limit reached. You have used all ${allowPerMonthPost} posts allowed for ${_monthYear}. Please wait until next month or upgrade your plan.`
          );
          setSnackbarSeverity("error");
          setOpenSnackbar(true);
          return;
        }
      }
    }
    // ────────────────────────────────────────────────────────────

    // Block if token is already expired or will expire before scheduled time
    const { expiredNow, expiredBySchedule } = checkTokenExpiryForSchedule(
      selectedAccounts,
      scheduledDateTime
    );

    if (expiredNow.length > 0) {
      setErrorModalData({
        title: "Cannot Submit - Expired Tokens",
        accounts: expiredNow,
        scheduleDate: "",
        message: "The following account(s) have expired access tokens and need to be reconnected in Settings before you can submit this post."
      });
      setErrorModalOpen(true);
      return;
    }

    if (expiredBySchedule.length > 0) {
      const scheduleLabel = dayjs(scheduledDateTime).format("MMMM D, YYYY");
      setErrorModalData({
        title: "Cannot Schedule - Token Expiry Conflict",
        accounts: expiredBySchedule,
        scheduleDate: scheduleLabel,
        message: `The following account(s) will have expired access tokens before your scheduled date of ${scheduleLabel}. Please reconnect these accounts or choose an earlier date.`
      });
      setErrorModalOpen(true);
      return;
    }
    const apiPlatforms = platforms.map(convertPlatformForAPI);

    // FIXED: Better handling of mediaFiles to ensure s3Url is always present
    const mediaFiles = mediaFile
      ? (Array.isArray(mediaFile) ? mediaFile : [mediaFile]).map((file) => {
          // Validate that S3 URL exists
          if (!file.s3Url) {
            console.warn("File missing s3Url:", file.originalName || file.name);
            throw new Error(
              `File "${
                file.originalName || file.name
              }" is missing upload URL. Please re-upload this file.`
            );
          }

          return {
            url: URL.createObjectURL(file),
            previewUrl: URL.createObjectURL(file),
            s3Url: file.s3Url,
            fileName: file.uniqueName || file.name,
            originalName: file.originalName || file.name,
            type: file.type,
            size: file.size,
          };
        })
      : [];

    // FIXED: Ensure mediaUrls contains only valid URLs
    const mediaUrls = mediaFiles.map((file) => file.s3Url).filter(Boolean);

    // Validation: Check if we lost any URLs
    if (mediaFiles.length > 0 && mediaUrls.length !== mediaFiles.length) {
      setSnackbarMessage(
        "Some files are missing upload URLs. Please re-upload all files."
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    const selectedAccountsData = selectedAccounts.map((accountId) => {
      const account = socialMediaAccounts.find(
        (acc) => acc.accountId === accountId
      );
      return {
        accountId: account.accountId,
        accountName: account.accountName,
        provider: account.provider,
        status: account.status,
      };
    });
    setPreviewData({
      type: "safeLaterPreview",
      platforms: apiPlatforms,
      contentType,
      postContent,
      accounts: selectedAccountsData,
      selectedAccounts,
      scheduleDateTime: scheduledDateTime.toISOString(),
      mediaFileCount: mediaFiles.length,
      mediaUrls: mediaUrls, // Now guaranteed to have valid URLs
      mediaFiles,
      clientId: getAuthTokenPayload()?.clientId,
      requiresApproval: true,
    });
    setPreviewModalOpen(true);
  };

  // Confirm posting
  const confirmPosting = async () => {
    try {
      // Double-check token expiry before final submission
      const { expiredNow, expiredBySchedule } = checkTokenExpiryForSchedule(
        previewData?.selectedAccounts || [],
        previewData?.scheduleDateTime || scheduledDateTime
      );

      if (expiredNow.length > 0) {
        setErrorModalData({
          title: "Cannot Submit - Expired Tokens",
          accounts: expiredNow,
          scheduleDate: "",
          message: "The following account(s) have expired access tokens and need to be reconnected in Settings before you can submit this post."
        });
        setErrorModalOpen(true);
        setPreviewModalOpen(false);
        return;
      }

      if (expiredBySchedule.length > 0) {
        const scheduleLabel = dayjs(
          previewData?.scheduleDateTime || scheduledDateTime
        ).format("MMMM D, YYYY");
        setErrorModalData({
          title: "Cannot Schedule - Token Expiry Conflict",
          accounts: expiredBySchedule,
          scheduleDate: scheduleLabel,
          message: `The following account(s) will have expired access tokens before your scheduled date of ${scheduleLabel}. Please reconnect these accounts or choose an earlier date.`
        });
        setErrorModalOpen(true);
        setPreviewModalOpen(false);
        return;
      }

      setShowLoader(true);
      setPreviewModalOpen(false);
      setLoadingPhase("preparing");
      setLoadingMessage("Preparing your post for approval...");
      setLoadingProgress(20);
      await new Promise((r) => setTimeout(r, 700));
      setLoadingPhase("processing");
      setLoadingMessage("Processing media files...");
      setLoadingProgress(50);
      await new Promise((r) => setTimeout(r, 600));
      setLoadingPhase("submitting");
      setLoadingMessage("Submitting for approval workflow...");
      setLoadingProgress(80);
      const approvalResponse = await submitForApprovalRunLaterPost(previewData);
      if (!approvalResponse.success)
        throw new Error(approvalResponse.message || "Submission failed");
      setLoadingPhase("complete");
      setLoadingMessage("Successfully submitted for approval!");
      setLoadingProgress(100);
      await new Promise((r) => setTimeout(r, 800));
      setShowLoader(false);
      setShowConfirmation(true);
      setTimeout(() => {
        clearForm();
        setShowConfirmation(false);
        setLoadingPhase("");
        setLoadingMessage("");
        setLoadingProgress(0);
      }, 2500);
    } catch (error) {
      setShowLoader(false);
      setShowConfirmation(false);
      setSnackbarMessage(error.message || "Failed to submit post");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      setLoadingPhase("");
      setLoadingMessage("");
      setLoadingProgress(0);
    }
  };

  // const clearForm = () => {
  //   setPostContent("");
  //   setPlatforms([]);
  //   setContentType("");
  //   setMediaFile(null);
  //   setScheduledDateTime(null);
  //   setSelectedAccounts([]);
  // };

  // Update your existing clearForm function
  const clearForm = () => {
    setPostContent("");
    setPlatforms([]);
    setContentType("");
    setMediaFile(null);
    setScheduledDateTime(null);
    setSelectedAccounts([]);
    // Clear Instagram cropper via hook
    resetCropping();
  };

  // Close handlers
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  const closePreviewModal = () => {
    setPreviewModalOpen(false);
  };

  // Add this to fix no-undef for closeImageModal
  const closeImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
  };

  // Get available platforms and accounts
  const availablePlatforms = getAvailablePlatforms(contentType);

  const getAvailableAccounts = () => {
    if (platforms.length === 0) return [];

    const apiPlatforms = platforms.map(convertPlatformForAPI);

    return socialMediaAccounts.filter((account) => {
      return account.status === 'Active' && apiPlatforms.some(
        (platform) => account.provider.toLowerCase() === platform.toLowerCase()
      );
    });
  };

  const availableAccounts = getAvailableAccounts();

  // Check if access tokens are expired now or by scheduled date
  const checkTokenExpiryForSchedule = (selectedAccountIds, scheduleDateTime) => {
    const today = dayjs().startOf("day");
    const scheduleDay = scheduleDateTime
      ? dayjs(scheduleDateTime).startOf("day")
      : null;
    const expiredNow = [];
    const expiredBySchedule = [];

    console.log("=== Token Expiry Check ===");
    console.log("Today:", today.format("YYYY-MM-DD"));
    console.log("Schedule Day:", scheduleDay ? scheduleDay.format("YYYY-MM-DD") : "null");
    console.log("Selected Account IDs:", selectedAccountIds);

    selectedAccountIds.forEach((accountId) => {
      const account = socialMediaAccounts.find(
        (acc) => acc.accountId === accountId
      );

      if (!account) {
        console.log(`Account ${accountId} not found in socialMediaAccounts`);
        return;
      }

      const tokenExpiry =
        account.accessTokenExpiry || account.tokenExpiry || account.expiry;

      console.log(`Account: ${account.accountName} (${account.provider})`);
      console.log(`  - accountId: ${account.accountId}`);
      console.log(`  - accessTokenExpiry: ${account.accessTokenExpiry}`);
      console.log(`  - tokenExpiry: ${account.tokenExpiry}`);
      console.log(`  - Using tokenExpiry: ${tokenExpiry}`);

      if (account && tokenExpiry) {
        const expiryDate = dayjs(tokenExpiry).startOf("day");
        console.log(`  - Expiry Date: ${expiryDate.format("YYYY-MM-DD")}`);

        if (expiryDate.isBefore(today)) {
          console.log(`  - Status: EXPIRED NOW`);
          expiredNow.push({
            accountName: account.accountName,
            provider: account.provider,
            expiry: tokenExpiry,
          });
        } else if (scheduleDay && expiryDate.isBefore(scheduleDay)) {
          // Token expires BEFORE the scheduled date - not allowed
          console.log(
            `  - Status: WILL EXPIRE BEFORE SCHEDULE (${expiryDate.format("YYYY-MM-DD")} < ${scheduleDay.format("YYYY-MM-DD")})`
          );
          expiredBySchedule.push({
            accountName: account.accountName,
            provider: account.provider,
            expiry: tokenExpiry,
          });
        } else {
          console.log(`  - Status: VALID (token expires on or after schedule date)`);
        }
      } else {
        console.log(`  - Status: NO TOKEN EXPIRY FOUND`);
      }
    });

    console.log("Expired Now:", expiredNow);
    console.log("Expired By Schedule:", expiredBySchedule);
    console.log("=========================");

    return { expiredNow, expiredBySchedule };
  };

  // ADDED: Function to remove a platform
  const removePlatform = (platformToRemove) => {
    const newPlatforms = platforms.filter(
      (platform) => platform !== platformToRemove
    );
    setPlatforms(newPlatforms);
    // Clear selected accounts when platforms change
    setSelectedAccounts([]);
  };

  // UPDATED: Function to remove a social media account with complete event isolation
  const removeAccount = (accountToRemove) => {
    const newAccounts = selectedAccounts.filter(
      (account) => account !== accountToRemove
    );
    setSelectedAccounts(newAccounts);
  };

  // Calendar handlers - NEW
  const openCalendar = () => {
    setCalendarOpen(true);
  };

  const closeCalendar = () => {
    setCalendarOpen(false);
  };

  const handleCalendarDateTimeSelect = (dateTime) => {
    setScheduledDateTime(dayjs(dateTime));
    setCalendarOpen(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {/* Replace top-level cropper usage and all dialogs below with a single wrapper */}
      <Box sx={{ py: 5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
        <Container maxWidth="md">
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "#4B5AB6", mb: 0.5 }}
            >
              <SecurityIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Safe Post - Approval Workflow
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#64748b", fontWeight: 400 }}
            >
              Submit your post for approval before scheduling to social media
              platforms
            </Typography>
          </Box>

          {/* Accounts Error Alert */}
          {accountsError && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 2 }}
              action={
                <Button
                  size="small"
                  onClick={fetchSocialMediaAccounts}
                  disabled={loadingAccounts}
                  startIcon={
                    loadingAccounts ? (
                      <CircularProgress size={16} />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                >
                  Retry
                </Button>
              }
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Error Loading Accounts
              </Typography>
              <Typography variant="body2">{accountsError}</Typography>
            </Alert>
          )}

          <Paper
            elevation={8}
            sx={{
              borderRadius: 4,
              boxShadow: "0 8px 32px rgba(75,90,182,0.16)",
              p: 0,
              maxWidth: 700,
              mx: "auto",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: "linear-gradient(90deg, #4B5AB6 0%, #6366f1 100%)",
                color: "#fff",
                p: 3,
                textAlign: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <ScheduleIcon />
                Submit Post for Approval
              </Typography>
            </Box>

            <Divider />

            <Box sx={{ p: 4, background: "#fff" }}>
              <Grid container spacing={2}>
                {/* Content Type - FIRST */}
                <Grid item xs={12}>
                  <FormControl fullWidth sx={{ mb: 2 }} error={!contentType}>
                    <InputLabel>Content Type *</InputLabel>
                    <Select
                      required
                      value={contentType}
                      onChange={(e) => handleContentTypeChange(e.target.value)}
                      label="Content Type *"
                      sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}
                    >
                      <MenuItem value="Content Only">Content Only</MenuItem>
                      <MenuItem value="Image">Image</MenuItem>
                      {/* Phase2 */}
                      {/* <MenuItem value="Video">Video</MenuItem> */}
                    </Select>
                    {!contentType && (
                      <FormHelperText>
                        Please select a content type first
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Platforms - SECOND */}
                <Grid item xs={12}>
                  <PlatformsSelect
                    contentType={contentType}
                    platforms={platforms}
                    availablePlatforms={availablePlatforms}
                    handlePlatformChange={handlePlatformChange}
                    removePlatform={removePlatform}
                  />
                </Grid>

                {/* Social Media Account Selection - THIRD */}
                <Grid item xs={12}>
                  <AccountsSelect
                    contentType={contentType}
                    platforms={platforms}
                    loadingAccounts={loadingAccounts}
                    availableAccounts={availableAccounts}
                    selectedAccounts={selectedAccounts}
                    socialMediaAccounts={socialMediaAccounts}
                    handleAccountChange={handleAccountChange}
                    removeAccount={removeAccount}
                  />
                </Grid>

                {/* Post Content */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, color: "#1e293b" }}
                    >
                      Post Content
                    </Typography>

                    <Button
                      variant="outlined"
                      startIcon={<PreviewIcon />}
                      onClick={() => setTemplateModalOpen(true)}
                      disabled={!contentType}
                      sx={{
                        bgcolor: "#fff",
                        color: "#4B5AB6",
                        borderColor: "#4B5AB6",
                        fontWeight: 700,
                        borderRadius: 2,
                        "&:hover": { bgcolor: "#eef2ff" },
                        "&:disabled": {
                          bgcolor: "#f1f5f9",
                          color: "#94a3b8",
                          borderColor: "#cbd5e1",
                        },
                      }}
                    >
                      Choose Post Template
                    </Button>
                  </Box>

                  <TextField
                    placeholder="Write your post content here..."
                    required
                    multiline
                    rows={8}
                    fullWidth
                    value={postContent}
                    onChange={(e) => setPostContent(e.target.value)}
                    error={!postContent.trim()}
                    helperText={
                      !postContent.trim() ? "Post content is required" : ""
                    }
                    disabled={!contentType}
                    sx={{
                      mb: 2,
                      bgcolor: "#f8fafc",
                      borderRadius: 2,
                      fontSize: "1.1rem",
                      "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    }}
                    inputProps={{ "aria-label": "Post Content" }}
                  />

                  {!contentType && (
                    <Typography
                      variant="caption"
                      sx={{ display: "block", color: "#64748b", mt: 0.5 }}
                    >
                      Select content type first to choose templates
                    </Typography>
                  )}
                </Grid>

                {/* Media Upload Section - reverted to below Post Content */}
                {contentType && contentType !== "Content Only" && (
                  <Grid item xs={12}>
                    <MediaUploadSection
                      contentType={contentType}
                      isUploading={isUploading}
                      platforms={platforms}
                      selectedAccounts={selectedAccounts}
                      handleMediaUpload={handleMediaUpload}
                      mediaFile={mediaFile}
                      removeMediaFile={removeMediaFile}
                      removeAllMediaFiles={removeAllMediaFiles}
                    />
                  </Grid>
                )}

                {/* Scheduled Date Time Picker */}
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "stretch" }}>
                    <TextField
                      label="Selected Date & Time"
                      fullWidth
                      value={
                        scheduledDateTime
                          ? scheduledDateTime.format("MMMM DD, YYYY at HH:mm")
                          : ""
                      }
                      disabled={
                        !contentType ||
                        platforms.length === 0 ||
                        selectedAccounts.length === 0
                      }
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        bgcolor: "#f8fafc",
                        borderRadius: 2,
                        "& .MuiOutlinedInput-root": { borderRadius: 2 },
                      }}
                      error={!scheduledDateTime}
                      helperText={
                        !scheduledDateTime ? "Please select date and time" : ""
                      }
                    />

                    <Button
                      variant="contained"
                      onClick={openCalendar}
                      startIcon={<ScheduleIcon />}
                      disabled={
                        !contentType ||
                        platforms.length === 0 ||
                        selectedAccounts.length === 0
                      }
                      sx={{
                        bgcolor: "#4B5AB6",
                        fontWeight: 600,
                        px: 3,
                        py: 1.75,
                        minWidth: "fit-content",
                        whiteSpace: "nowrap",
                        height: "56px",
                        alignSelf: "flex-start",
                        "&:hover": { bgcolor: "#3a4891" },
                      }}
                    >
                      Select Date & Time
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                p: 3,
                background: "#f8fafc",
                gap: 2, // Add gap between buttons
              }}
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={clearForm}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  bgcolor: "#fff",
                  color: "#4B5AB6",
                  borderColor: "#4B5AB6",
                  "&:hover": { bgcolor: "#eef2ff" },
                }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SecurityIcon />
                  )
                }
                disabled={isSubmitting}
                sx={{
                  background: isSubmitting
                    ? "#94a3b8"
                    : "linear-gradient(90deg, #059669 0%, #10b981 100%)",
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  boxShadow: "0 2px 8px rgba(5,150,105,0.12)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    background: isSubmitting
                      ? "#94a3b8"
                      : "linear-gradient(90deg, #10b981 0%, #059669 100%)",
                    transform: isSubmitting ? "none" : "scale(1.04)",
                  },
                  "&:disabled": {
                    color: "#fff",
                    cursor: "not-allowed",
                  },
                }}
                onClick={handleSubmitForApproval}
              >
                {isSubmitting ? "Submitting..." : "Submit for Approval"}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>

      <SafePostDialogs
        // Instagram cropper
        instagramCropperOpen={instagramCropperOpen}
        handleInstagramCropperClose={handleInstagramCropperClose}
        currentCroppingImage={currentCroppingImage}
        handleInstagramCropSave={handleInstagramCropSave}
        // Template selection
        templateModalOpen={templateModalOpen}
        closeTemplateModal={() => setTemplateModalOpen(false)}
        templateSearchQuery={templateSearchQuery}
        setTemplateSearchQuery={setTemplateSearchQuery}
        filteredTemplates={filteredTemplates}
        selectTemplate={selectTemplate}
        // Calendar
        calendarOpen={calendarOpen}
        closeCalendar={closeCalendar}
        calendarDialogContentRef={calendarDialogContentRef}
        scheduledDateTime={scheduledDateTime}
        handleCalendarDateTimeSelect={handleCalendarDateTimeSelect}
        drawerContainerEl={null}
        // Lightweight preview
        previewModalOpen={previewModalOpen}
        closePreviewModal={closePreviewModal}
        platforms={platforms}
        postContent={postContent}
        contentType={contentType}
        mediaFile={mediaFile}
        confirmPosting={confirmPosting}
        // Snackbar
        openSnackbar={openSnackbar}
        handleCloseSnackbar={handleCloseSnackbar}
        snackbarSeverity={snackbarSeverity}
        snackbarMessage={snackbarMessage}
        // Image preview
        imageModalOpen={imageModalOpen}
        selectedImage={selectedImage}
        closeImageModal={closeImageModal}
        // Loader
        showLoader={showLoader}
        loadingMessage={loadingMessage}
        loadingProgress={loadingProgress}
        // Confirmation
        showConfirmation={showConfirmation}
        onCloseConfirmation={() => setShowConfirmation(false)}
        // Error modal
        errorModalOpen={errorModalOpen}
        errorModalData={errorModalData}
        onCloseErrorModal={() => setErrorModalOpen(false)}
      />

      {/* Keep your advanced PreviewPostDialog if you use it */}
      {/* ...existing code... */}
    </LocalizationProvider>
  );
};

export default SafePost;
